"use client";

import type { HTMLAttributes, FC, Ref } from "react";
import { mergeClasses } from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import type {
  BorderProp,
  ClassNameProp,
  EdgesProps,
  ElevatedProp,
  RequiredChildrenProp,
} from "../../props";
import { Edges } from "../Edges/Edges";
import { chipStyle, edgeContentStyle } from "./Chip.css";
import { borders } from "../../theme/css/borders.css";
import { elevations } from "../../theme/css/elevations.css";
import { focusVisible } from "../../theme/css/focusVisible.css";
import { Text } from "../Text/Text";

export type ChipProps = HTMLAttributes<HTMLDivElement> &
  RequiredChildrenProp &
  ClassNameProp &
  ElevatedProp &
  RecipeVariants<typeof chipStyle> &
  EdgesProps &
  BorderProp & {
    shrink?: boolean;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * A chip is a small, interactive element that represents a piece of information.
 */
export const Chip: FC<ChipProps> = ({
  trailing,
  leading,
  variant = "primary",
  size,
  elevated = false,
  border,
  alignSelf,
  justifySelf,
  shrink,
  className,
  children,
  ref,
  ...props
}) => (
  <div
    ref={ref}
    className={mergeClasses(
      chipStyle({ size, variant, alignSelf, justifySelf, shrink }),
      border ? borders[border] : undefined,
      focusVisible,
      elevated ? elevations.l : undefined,
      className,
    )}
    {...props}
  >
    <Edges
      trailing={trailing}
      leading={leading}
      className={edgeContentStyle}
      hideOverflow
    >
      <Text ellipsis variant={size === "l" ? "bodyM" : "bodyS"}>
        {children}
      </Text>
    </Edges>
  </div>
);
